import type { ReploShopifyProduct } from "replo-runtime/shared/types";

import React from "react";

interface ProductTileContextType {
  productData: ReploShopifyProduct | undefined;
  selectedVariantId: string | null;
  selectedVariant: ReploShopifyProduct["variants"][0] | null;
  selectOptionValue: (
    optionKey: "option1" | "option2" | "option3",
    value: string,
  ) => void;
  getProductOptionByKey: (
    optionKey: string,
  ) => ReploShopifyProduct["options"][0] | undefined;
}

const ProductTileContext = React.createContext<ProductTileContextType | null>(
  null,
);

export const useProductTileContext = () => {
  const context = React.useContext(ProductTileContext);
  if (!context) {
    throw new Error(
      "useProductTileContext must be used within a ProductTileProvider",
    );
  }
  return context;
};

interface ProductTileProviderProps {
  productData: ReploShopifyProduct | undefined;
  selectedVariantId: string | null;
  onVariantChange: (variantId: string) => void;
  children: React.ReactNode;
}

export const ProductTileProvider: React.FC<ProductTileProviderProps> = ({
  productData,
  selectedVariantId,
  onVariantChange,
  children,
}) => {
  const selectedVariant = React.useMemo(() => {
    if (!productData || !selectedVariantId) {
      return null;
    }
    return (
      productData.variants.find(
        (v) => String(v.id) === String(selectedVariantId),
      ) ?? null
    );
  }, [productData, selectedVariantId]);

  const selectOptionValue = React.useCallback(
    (optionKey: "option1" | "option2" | "option3", value: string) => {
      if (!productData || !selectedVariant) {
        return;
      }

      const selectedOptions = {
        option1: selectedVariant.option1,
        option2: selectedVariant.option2,
        option3: selectedVariant.option3,
      };
      selectedOptions[optionKey] = value;

      const newSelectedVariant = productData.variants.find(
        (variant) =>
          variant.option1 === selectedOptions.option1 &&
          variant.option2 === selectedOptions.option2 &&
          variant.option3 === selectedOptions.option3,
      );

      if (newSelectedVariant) {
        onVariantChange(String(newSelectedVariant.id));
      }
    },
    [productData, selectedVariant, onVariantChange],
  );

  const getProductOptionByKey = React.useCallback(
    (optionKey: string) => {
      if (!productData) {
        return undefined;
      }
      return productData.options.find((option) => option.key === optionKey);
    },
    [productData],
  );

  const value = React.useMemo(
    () => ({
      productData,
      selectedVariantId,
      selectedVariant,
      selectOptionValue,
      getProductOptionByKey,
    }),
    [
      productData,
      selectedVariantId,
      selectedVariant,
      selectOptionValue,
      getProductOptionByKey,
    ],
  );

  return (
    <ProductTileContext.Provider value={value}>
      {children}
    </ProductTileContext.Provider>
  );
};
